import {
  AlertSeverity,
  ButtonType,
  CovAlert,
  CovButton,
  CovIcon,
  CovLoading,
  CovTextField,
  IconName,
} from "@cx-component-library/react-components";
import {
  Box,
  ButtonBase,
  InputAdornment,
  Popover,
  type SxProps,
  type Theme,
  Typography,
} from "@mui/material";
import type { CollectionBookmarkStatus } from "hooks/use-collection-bookmark";
import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CollectionMembership } from "service/collections";
import { AddCollectionIcon, VisitIcon } from "./CollectionPopoverIcons";

export interface SelectCollectionPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  status: CollectionBookmarkStatus;
  collections: CollectionMembership[];
  pendingIds: ReadonlySet<string>;
  onToggle: (collection: CollectionMembership) => void;
  /** "Create new collection" — the host opens the Create New Collection dialog. */
  onCreate: () => void;
  onRetry: () => void;
  allCollectionsUrl: string;
}

const divider = "1px solid var(--alias-stroke-on-white-default, #d9d9d9)";
const rowText = {
  fontSize: "var(--body-b2-14-regular-size, 14px)",
  lineHeight: "normal",
  color: "var(--alias-text-on-white-primary, #000)",
};

const rowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: "var(--spacing16, 16px)",
  width: "100%",
  minHeight: "var(--spacing44, 44px)",
  padding: "var(--spacing12, 12px) var(--spacing16, 16px)",
  justifyContent: "flex-start",
  textAlign: "left",
  ...rowText,
  "&:hover, &.Mui-focusVisible": {
    backgroundColor: "var(--alias-surface-semantics-cyan-lowest, #d9f1fb)",
  },
  "&.Mui-disabled": { opacity: 0.6 },
};

const mutedSx: SxProps<Theme> = {
  ...rowText,
  color: "var(--alias-text-on-white-disable, #999)",
  padding: "var(--spacing12, 12px) var(--spacing16, 16px)",
};

// Figma's search field is 32px tall; MUI `small` is 40px.
const compactFieldSx: SxProps<Theme> = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    paddingRight: "var(--spacing12, 12px)",
    borderRadius: "var(--spacing4, 4px)",
  },
  "& .MuiOutlinedInput-input": {
    padding: "var(--spacing8, 8px) var(--spacing12, 12px)",
    fontSize: "var(--body-b2-14-regular-size, 14px)",
    lineHeight: "normal",
    height: "auto",
  },
};

export const SelectCollectionPopover: FC<SelectCollectionPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  status,
  collections,
  pendingIds,
  onToggle,
  onCreate,
  onRetry,
  allCollectionsUrl,
}) => {
  const { t } = useTranslation();
  const [term, setTerm] = useState("");

  const filtered = useMemo(() => {
    const needle = term.trim().toLocaleLowerCase();
    return needle
      ? collections.filter((c) => c.name.toLocaleLowerCase().includes(needle))
      : collections;
  }, [collections, term]);

  const isEmpty = status === "ready" && collections.length === 0;

  const handleExited = () => setTerm("");

  const renderList = () => {
    if (status === "loading" || status === "idle") {
      return (
        <Box
          sx={{ position: "relative", height: "var(--spacing88, 88px)" }}
          data-testid="collection-popover-loading"
        >
          <CovLoading coverParent />
        </Box>
      );
    }
    if (status === "error") {
      return (
        <Box sx={{ padding: "var(--spacing8, 8px) var(--spacing16, 16px)" }}>
          <CovAlert
            severity={AlertSeverity.ERROR}
            action={
              <CovButton
                buttonType={ButtonType.SECONDARY_OUTLINE}
                onClick={onRetry}
              >
                {t("src_collection_popover_retry")}
              </CovButton>
            }
          >
            {t("src_collection_popover_load_error")}
          </CovAlert>
        </Box>
      );
    }
    if (filtered.length === 0) {
      return (
        <Typography sx={mutedSx}>
          {t("src_collection_popover_no_match", { term: term.trim() })}
        </Typography>
      );
    }
    return (
      <Box
        role="group"
        aria-label={String(t("src_collection_popover_title"))}
        sx={{
          // About five rows, then the list scrolls on its own.
          maxHeight: "calc(var(--spacing44, 44px) * 5)",
          overflowY: "auto",
        }}
      >
        {filtered.map((collection) => (
          <ButtonBase
            key={collection.collectionId}
            role="checkbox"
            aria-checked={collection.contains}
            disabled={pendingIds.has(collection.collectionId)}
            onClick={() => onToggle(collection)}
            sx={rowSx}
          >
            <Box
              component="span"
              sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}
            >
              {collection.name}
            </Box>
            <Box
              component="span"
              sx={{
                display: "flex",
                width: "var(--spacing24, 24px)",
                justifyContent: "center",
              }}
            >
              {collection.contains && (
                <CovIcon
                  iconName={IconName.DONE}
                  iconSize="sm"
                  iconColor="var(--alias-icon-on-white-brand, #009fe4)"
                  ariaHidden
                />
              )}
            </Box>
          </ButtonBase>
        ))}
      </Box>
    );
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      TransitionProps={{ onExited: handleExited }}
      slotProps={{
        paper: {
          sx: {
            width: "298px",
            maxWidth: "calc(100vw - var(--spacing32, 32px))",
            // No radius/shadow tokens exist; values are Figma's (S_7).
            borderRadius: "2px",
            boxShadow:
              "0 2px 24px rgba(0, 0, 0, 0.08), 0 28px 32px 1px rgba(0, 0, 0, 0.12)",
            backgroundColor: "var(--alias-surface-lowest, #fff)",
            paddingBottom: "var(--spacing4, 4px)",
          },
        },
      }}
    >
      <Typography
        component="h2"
        sx={{
          padding:
            "var(--spacing12, 12px) var(--spacing16, 16px) var(--spacing10, 10px)",
          borderBottom: divider,
          fontSize: "var(--body-b3-12-regular-size, 12px)",
          lineHeight: "normal",
          textTransform: "uppercase",
          color: "var(--alias-text-on-white-primary, #000)",
        }}
      >
        {t("src_collection_popover_title")}
      </Typography>

      {isEmpty ? (
        <Typography sx={mutedSx}>
          {t("src_collection_popover_empty")}
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              padding: "var(--spacing8, 8px) var(--spacing16, 16px)",
              borderBottom: divider,
            }}
          >
            <CovTextField
              size="small"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder={t("src_collection_popover_search")}
              inputProps={{ "aria-label": t("src_collection_popover_search") }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CovIcon
                      iconName={IconName.SEARCH}
                      iconSize="sm"
                      ariaHidden
                    />
                  </InputAdornment>
                ),
              }}
              sx={compactFieldSx}
            />
          </Box>
          {renderList()}
        </>
      )}

      <Box sx={{ borderTop: divider }}>
        <ButtonBase
          onClick={onCreate}
          aria-haspopup="dialog"
          sx={{ ...rowSx, borderBottom: divider } as SxProps<Theme>}
        >
          <AddCollectionIcon />
          {t("src_collection_popover_create")}
        </ButtonBase>
        <ButtonBase
          component="a"
          href={allCollectionsUrl}
          sx={{ ...rowSx, borderBottom: divider } as SxProps<Theme>}
        >
          <VisitIcon />
          {t("src_collection_popover_all")}
        </ButtonBase>
      </Box>
    </Popover>
  );
};
